/**
 * ChatTermSheetSelectionCard — inline term-sheet picker triggered by
 * request_term_sheet_selection.
 *
 * AI Chat V1.5. Driven by the `term_sheet_selection_prompt` SSE event
 * (NOT a tool_result — not registered in componentRegistry). Rendered
 * when a deal is in context and has 2+ analyzed term sheets.
 *
 * Click flow:
 *   1. clearTermSheetSelectionPrompt()
 *   2. send(synthetic confirmation with term_sheet_id + deal_id)
 *
 * Bank-name resolution: backend returns `bank_id` only. We use the
 * cached useBanks() lookup to map bank_id → bank.name. Falls back to
 * the original filename (extension stripped), then to "Term sheet #id".
 */
import { useMemo } from 'react';

import { useBanks } from '../../../api/hooks/useLookups';
import {
  useChatStore,
  type SelectableTermSheet,
} from '../../../stores/chatStore';
import { useChatStream } from '../hooks/useChatStream';

function formatUploadDate(isoString?: string | null): string {
  if (!isoString) {return '';}
  try {
    return new Date(isoString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function fallbackLabel(ts: SelectableTermSheet): string {
  if (ts.filename) {
    return ts.filename.replace(/\.[^.]+$/, '');
  }
  return `Term sheet #${ts.id}`;
}

export function ChatTermSheetSelectionCard() {
  const prompt = useChatStore((s) => s.termSheetSelectionPrompt);
  const clearTermSheetSelectionPrompt = useChatStore(
    (s) => s.clearTermSheetSelectionPrompt
  );
  const { send } = useChatStream();
  const { data: banks } = useBanks();

  const bankMap = useMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {};
    for (const b of banks ?? []) {
      map[b.id] = b.name;
    }
    return map;
  }, [banks]);

  if (!prompt) {return null;}

  const displayLabel = (ts: SelectableTermSheet): string => {
    if (ts.bank_id != null && bankMap[ts.bank_id]) {
      return bankMap[ts.bank_id];
    }
    return fallbackLabel(ts);
  };

  const handleSelect = (ts: SelectableTermSheet) => {
    clearTermSheetSelectionPrompt();
    const label = displayLabel(ts);
    // V1.4.1 — synthetic: hidden from the chat thread, sent to the
    // backend so the model gets the explicit term_sheet_id binding.
    send({
      text:
        `I'm asking about the "${label}" term sheet (term_sheet_id: ${ts.id}, ` +
        `deal_id: ${prompt.deal_id}). Please confirm and answer.`,
      synthetic: true,
    });
  };

  if (prompt.term_sheets.length === 0) {
    return (
      <div className="mt-2 border border-border bg-surface-subtle px-3 py-3 text-body-sm text-txt-secondary">
        No analyzed term sheets found for this deal yet.
      </div>
    );
  }

  return (
    <div className="mt-2 border border-border bg-surface-card">
      <div className="border-b border-border px-3 py-2">
        <p className="text-body-sm text-txt-secondary">
          Which term sheet are you asking about?
        </p>
      </div>
      <div className="flex flex-col divide-y divide-border">
        {prompt.term_sheets.map((ts) => {
          const label = displayLabel(ts);
          const meta = [
            ts.version_number != null ? `v${ts.version_number}` : null,
            formatUploadDate(ts.uploaded_at),
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <button
              key={ts.id}
              type="button"
              onClick={() => handleSelect(ts)}
              className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-body font-semibold text-txt-primary">
                  {label}
                </p>
                {meta && (
                  <p className="text-body-sm text-txt-secondary">{meta}</p>
                )}
              </div>
              <span className="shrink-0 text-body-sm text-txt-tertiary">
                Select →
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
